import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api } from '../api/client';
import ConfirmDialog from '../components/ConfirmDialog';
import { useT } from '../i18n/i18n';
import { roleLabel, type Role, type Staff } from './schoolApi';

export default function StaffTab() {
  const t = useT();
  const [staff, setStaff] = useState<Staff[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('TEACHER');
  const [removing, setRemoving] = useState<Staff | null>(null);

  const load = useCallback(async () => {
    try {
      setStaff(await api<Staff[]>('/school/staff', { token: true }));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('Could not load the staff list.'));
    }
  }, [t]);

  useEffect(() => { void load(); }, [load]);

  async function run(action: () => Promise<unknown>) {
    setError(null);
    setBusy(true);
    try {
      await action();
      await load();
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That did not work. Please try again.'));
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function add(e: FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    const ok = await run(() => api('/school/staff', { method: 'POST', token: true, body: { email: email.trim(), name: name.trim() || null, role } }));
    if (ok) { setEmail(''); setName(''); setRole('TEACHER'); }
  }

  function status(s: Staff): string {
    if (!s.active) return t('Access removed');
    if (!s.signedIn) return t('Has not signed in yet');
    if (s.lastSeenAt) return t('Last signed in {date}', { date: new Date(s.lastSeenAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) });
    return t('Signed in');
  }

  return (
    <div className="spaced">
      <div className="card">
        <h3>{t('Staff')}</h3>
        <p className="field-hint" style={{ marginTop: 0 }}>
          {t('Staff sign in with their school account. Anyone from the school who signs in is added here as a teacher with no classes, so they see no pupils until you add them to a class.')}
        </p>
        {error && <div className="error" role="alert">{error}</div>}
        {staff === null ? (
          <p className="muted">{t('Loading…')}</p>
        ) : (
          <ul className="list">
            {staff.map((s) => (
              <li className="list-item" key={s.id}>
                <div className="list-item-text">
                  <div className="name">{s.name ?? s.email}{s.you && <> ({t('you')})</>}</div>
                  <div className="muted">{s.email}</div>
                  <div className="muted">{t(roleLabel(s.role))} · {status(s)}</div>
                </div>
                {!s.you && (
                  <div className="toolbar">
                    {s.active ? (
                      <>
                        <label className="visually-hidden" htmlFor={`staff-role-${s.id}`}>{t('Role')}</label>
                        <select
                          id={`staff-role-${s.id}`}
                          className="school-role"
                          value={s.role}
                          disabled={busy}
                          onChange={(e) => void run(() => api(`/school/staff/${s.id}`, { method: 'PATCH', token: true, body: { role: e.target.value } }))}
                        >
                          <option value="TEACHER">{t('Teacher')}</option>
                          <option value="ADMIN">{t('School admin')}</option>
                        </select>
                        <button type="button" className="btn quiet-danger" onClick={() => setRemoving(s)} disabled={busy}>{t('Remove access')}</button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="btn secondary"
                        disabled={busy}
                        onClick={() => void run(() => api(`/school/staff/${s.id}`, { method: 'PATCH', token: true, body: { active: true } }))}
                      >
                        {t('Give access again')}
                      </button>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <form className="card spaced" onSubmit={add}>
        <h3>{t('Add a member of staff')}</h3>
        <p className="field-hint" style={{ marginTop: 0 }}>{t('Add someone before they first sign in to choose their role in advance.')}</p>
        <div className="field">
          <label htmlFor="staff-new-email">{t('School email address')}</label>
          <input id="staff-new-email" type="email" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="staff-new-name">{t('Name (optional)')}</label>
          <input id="staff-new-name" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="staff-new-role">{t('Role')}</label>
          <select id="staff-new-role" value={role} onChange={(e) => setRole(e.target.value as Role)}>
            <option value="TEACHER">{t('Teacher — sees the pupils in their classes')}</option>
            <option value="ADMIN">{t('School admin — sees every pupil and manages the school')}</option>
          </select>
        </div>
        <div className="toolbar">
          <button type="submit" className="btn" disabled={busy || !email.trim()}>{t('Add to staff')}</button>
        </div>
      </form>

      <ConfirmDialog
        open={removing !== null}
        title={t('Remove access for {name}?', { name: removing?.name ?? removing?.email ?? '' })}
        message={t('They are signed out and cannot use EasyLearning for this school. Nothing they recorded is deleted. You can give access again later.')}
        confirmLabel={t('Remove access')}
        destructive
        onConfirm={() => {
          const s = removing;
          setRemoving(null);
          if (s) void run(() => api(`/school/staff/${s.id}`, { method: 'PATCH', token: true, body: { active: false } }));
        }}
        onCancel={() => setRemoving(null)}
      />
    </div>
  );
}
